import { DomainEventSubscription } from './DomainEventBus';

/**
 * Manages a collection of event subscriptions, allowing for adding and unsubscribing from multiple subscriptions.
 */
export class EventSubscriptions {
    private subscriptions: DomainEventSubscription[] = [];

    /**
     * Creates an instance of the class, optionally initializing it with a list of domain event subscriptions.
     *
     * @param subscriptions - An optional array of `DomainEventSubscription` objects to initialize the subscriptions list.
     */
    constructor(subscriptions?: DomainEventSubscription[]) {
        if (subscriptions) {
            subscriptions.forEach((sub) => this.subscriptions.push(sub));
        }
    }

    /**
     * Gets all current domain event subscriptions.
     *
     * @returns An array of {@link DomainEventSubscription} objects representing all active subscriptions.
     */
    public get allSubscriptions(): DomainEventSubscription[] {
        return this.subscriptions;
    }

    /**
     * Adds a new event subscription to the list of subscriptions.
     *
     * @param subscription - The event subscription to add.
     */
    public add(subscription: DomainEventSubscription): void {
        this.subscriptions.push(subscription);
    }

    /**
     * Unsubscribes from a specific domain event subscription.
     *
     * Removes the provided subscription from the internal subscriptions list and calls its `unsubscribe` method.
     *
     * @param subscription - The domain event subscription to unsubscribe and remove.
     */
    public unsubscribe(subscription: DomainEventSubscription): void {
        const index = this.subscriptions.indexOf(subscription);
        if (index !== -1) {
            this.subscriptions[index].unsubscribe();
            this.subscriptions.splice(index, 1);
        }
    }

    /**
     * Unsubscribes from all active subscriptions managed by this instance.
     * Iterates through the `subscriptions` array, calling `unsubscribe()` on each subscription,
     * and then clears the array to remove all references.
     */
    public unsubscribeAll(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
    }
}
